import { cva } from 'cva';
import { UserIcon } from 'lucide-react';
import { Link, useLocation, useSearchParams } from 'react-router';

import appStrings from '@/assets/constants/appStrings-no.js';
import { routes } from '@/assets/constants/routes-no.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { useAuth } from '@/services/Auth/index.ts';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';

const cta = cva({
  base: [
    'flex h-12 cursor-pointer items-center justify-center gap-1 rounded-full border-2 px-6 font-semibold no-underline transition-colors active:duration-0',
  ],
  variants: {
    color: {
      blue: [
        'border-v1-primary-100 bg-v1-primary-100 text-white',
        'hover:bg-white hover:text-v1-background-200',
        'active:bg-v1-background-60 active:text-v1-background-200',
      ],
      white: [
        'border-v1-primary-20 bg-white text-v1-primary-100',
        'hover:bg-v1-background-60',
        'active:bg-v1-background-80',
      ],
    },
  },
});

interface NavbarButtonsProps {
  className?: string;
  onNavigate?: () => void;
}

export const NavbarButtons = ({ className, onNavigate }: NavbarButtonsProps) => {
  const { toggleChat } = useBrainSession();
  const { isAnonymous, login } = useAuth();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const myScooprHref = isAnonymous ? routes.MY_SCOOPR_LOGIN : routes.MY_SCOOPR;
  const isOnMyScooprLogin = isAnonymous && pathname === routes.MY_SCOOPR_LOGIN;

  const startLogin = () => {
    onNavigate?.();
    sendUserPortalEvent('user_login_started');
    void login('SCRIVE', searchParams.get('return_to'));
  };

  const myScooprLabel = (
    <>
      {appStrings.MENU.MY_SCOOPR} <UserIcon className="-mr-1 size-5" />
    </>
  );

  return (
    <div className={className}>
      {isOnMyScooprLogin ? (
        <button type="button" className={cta({ color: 'white' })} onClick={startLogin}>
          {myScooprLabel}
        </button>
      ) : (
        <Link
          prefetch="intent"
          to={`${myScooprHref}${search}`}
          className={cta({ color: 'white' })}
          onClick={onNavigate}
        >
          {myScooprLabel}
        </Link>
      )}
      <button
        type="button"
        className={cta({ color: 'blue' })}
        onClick={() => {
          onNavigate?.();
          sendUserPortalEvent('top_cta_clicked');
          toggleChat({
            goal: 'debt_check',
            step: 'skip_start',
          });
        }}
      >
        Søk om lavere rente
      </button>
    </div>
  );
};
